import {
  CheckIcon,
  FileDownIcon,
  ImageIcon,
  Share2Icon,
  SparklesIcon,
  Trash2Icon,
  UploadIcon,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/new-ui/button';

import { DropdownMediaButtons } from './dropdown-media-buttons';

type EstateMediaButtonsProps = {
  type: 'estate';
  estateId: string;
};

type ProjectMediaButtonsProps = {
  type: 'project';
  projectId: string;
};

export type MediaButtonsProps = {
  isThumbnailDisabled?: boolean;
  // set while selecting when the picked item cannot be rendered (a PDF floor plan)
  isImageRenderingDisabled?: boolean;
  canWriteMedia?: boolean;
  isSelecting?: boolean;
  selectedCount?: number;
  onSelect?: () => void;
  onCancelSelect?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onUpload?: () => void;
  onChooseThumbnail?: () => void;
  onClickImageRendering?: () => void;
} & (EstateMediaButtonsProps | ProjectMediaButtonsProps);

// Section toolbar, rendered inside the `new-ui` header row of the media section pages
export const MediaButtons = ({
  type = 'estate',
  canWriteMedia = true,
  isThumbnailDisabled,
  isImageRenderingDisabled = false,
  isSelecting = false,
  selectedCount = 0,
  onSelect,
  onCancelSelect,
  onDelete,
  onShare,
  onDownload,
  onUpload,
  onChooseThumbnail,
  onClickImageRendering,
}: MediaButtonsProps) => {
  const { t } = useTranslation();

  if (isSelecting) {
    // AI rendering works on one picked image, so it stays available while selecting
    const showRendering = type === 'estate' && Boolean(canWriteMedia && onClickImageRendering);
    return (
      <>
        {showRendering ? (
          <Button
            variant="outline"
            className="gap-1"
            disabled={selectedCount !== 1 || isImageRenderingDisabled}
            onClick={onClickImageRendering}
          >
            <SparklesIcon className="h-4 w-4" />
            {t('aiImageRendering')}
          </Button>
        ) : null}
        {/* Shown right away, usable once something is selected */}
        <Button
          variant="outline"
          className="gap-1"
          disabled={selectedCount === 0}
          onClick={onDownload}
        >
          <FileDownIcon className="h-4 w-4" />
          {t('download')}
        </Button>
        {canWriteMedia ? (
          <Button
            variant="destructive"
            className="gap-1"
            disabled={selectedCount === 0}
            onClick={onDelete}
          >
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </Button>
        ) : null}
        <Button variant="outline" className="gap-1" onClick={onCancelSelect}>
          <XIcon className="h-4 w-4" />
          {t('cancel')}
        </Button>
      </>
    );
  }

  return (
    <>
      {onSelect ? (
        <Button variant="outline" className="gap-1" onClick={onSelect}>
          <CheckIcon className="h-4 w-4" />
          {t('select')}
        </Button>
      ) : null}
      <DropdownMediaButtons
        type={type}
        canWriteMedia={canWriteMedia}
        isThumbnailDisabled={isThumbnailDisabled}
        onChooseThumbnail={onChooseThumbnail}
        onClickImageRendering={onClickImageRendering}
        onShare={onShare}
      />
      {canWriteMedia ? (
        <Button className="gap-1" onClick={onUpload}>
          <UploadIcon className="h-4 w-4" />
          {t('uploadMedia')}
        </Button>
      ) : null}
    </>
  );
};

/**
 * Second toolbar row of a media section on wider screens: choose thumbnail, AI rendering and
 * share. Select and upload stay in the breadcrumb row; phones get these in the more menu.
 */
export const MediaExtraButtons = ({
  type = 'estate',
  canWriteMedia = true,
  isThumbnailDisabled,
  isSelecting = false,
  onShare,
  onChooseThumbnail,
  onClickImageRendering,
}: MediaButtonsProps) => {
  const { t } = useTranslation();

  const showThumbnail = Boolean(canWriteMedia && onChooseThumbnail);
  const showRendering = type === 'estate' && Boolean(canWriteMedia && onClickImageRendering);
  const showShare = type === 'estate' && Boolean(canWriteMedia && onShare);
  if (isSelecting || (!showThumbnail && !showRendering && !showShare)) return null;

  return (
    <div className="hidden flex-wrap items-center gap-2 md:flex">
      {/* Only pages that can choose a thumbnail pass the handler */}
      {showThumbnail ? (
        <Button
          variant="outline"
          className="gap-1"
          disabled={isThumbnailDisabled}
          onClick={onChooseThumbnail}
        >
          <ImageIcon className="h-4 w-4" />
          {t('chose-thumbnail')}
        </Button>
      ) : null}
      {showRendering ? (
        <Button variant="outline" className="gap-1" onClick={onClickImageRendering}>
          <SparklesIcon className="h-4 w-4" />
          {t('aiImageRendering')}
        </Button>
      ) : null}
      {showShare ? (
        <Button variant="outline" className="gap-1" onClick={onShare}>
          <Share2Icon className="h-4 w-4" />
          {t('share')}
        </Button>
      ) : null}
    </div>
  );
};
